import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import {
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  LibraryAdd as LibraryAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Accordion from '../UIKit/Accordion';
import JsonPartExport from '../UIKit/JsonPartExport';
import LessonJsonImport from '../UIKit/LessonJsonImport';
import LessonsArrayJsonImport from '../UIKit/LessonsArrayJsonImport';
import Button from '../UIKit/Button';
import TextField from '../UIKit/TextField';
import Select from '../UIKit/Select';
import LessonEditor from '../UIKit/LessonEditor';
import { modulesAPI } from '../../services/api';
import {
  isYouTubeUrl,
  getYouTubeId,
  playVideo,
  pauseAndResetVideo,
} from '../../utils/videoUtils';

// Кастомний хук для debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ModuleCard = memo(({
  moduleRef,
  moduleIndex,
  totalModules,
  isExpanded,
  localModuleFields,
  user,
  isDraft,
  showOrderField = false, // Показывать поле порядка модуля только в контексте курса
  onToggleExpanded,
  onModuleFieldChange,
  onRemoveModule,
  onAddLesson,
  onOpenLessonSelector,
  onLessonChange,
  onModuleOrderChange,
  onDraftToggle,
  onProfessionsSync, // Callback для синхронизации профессий с курсом
}) => {
  const { t } = useTranslation(['courses', 'common']);
  const module = moduleRef.module || moduleRef;
  const moduleKey = module._id || `module-${moduleIndex}`;

  // Локальное состояние для полей модуля
  const [localFields, setLocalFields] = useState({
    title: localModuleFields[`module-${moduleIndex}-title`] || module.title || '',
    description: localModuleFields[`module-${moduleIndex}-description`] || module.description || '',
    content: localModuleFields[`module-${moduleIndex}-content`] || module.content || '',
    previewImage: localModuleFields[`module-${moduleIndex}-previewImage`] || module.previewImage || '',
    previewImageFile: null,
    previewImagePreview: null,
    videoUrl: localModuleFields[`module-${moduleIndex}-videoUrl`] || module.videoUrl || '',
  });

  // State for module preview
  const [modulePreviewOpen, setModulePreviewOpen] = useState(false);

  // State for status dropdown
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // State for video playback - simple version
  const [showVideo, setShowVideo] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef(null);

  // Debounced значения для отправки в родительский компонент
  const debouncedTitle = useDebounce(localFields.title, 300);
  const debouncedDescription = useDebounce(localFields.description, 300);
  const debouncedContent = useDebounce(localFields.content, 300);
  const debouncedPreviewImage = useDebounce(localFields.previewImage, 300);
  const debouncedVideoUrl = useDebounce(localFields.videoUrl, 300);

  // Состояние для модального окна подтверждения удаления урока
  const [deleteLessonDialog, setDeleteLessonDialog] = useState({
    open: false,
    moduleId: null,
    lessonId: null,
    lessonIndex: null,
    lessonTitle: ''
  });

  // Отправляем debounced значения в родительский компонент
  useEffect(() => {
    onModuleFieldChange(moduleIndex, { target: { name: 'title', value: debouncedTitle } });
  }, [debouncedTitle, moduleIndex, onModuleFieldChange]);

  useEffect(() => {
    onModuleFieldChange(moduleIndex, { target: { name: 'description', value: debouncedDescription } });
  }, [debouncedDescription, moduleIndex, onModuleFieldChange]);

  useEffect(() => {
    onModuleFieldChange(moduleIndex, { target: { name: 'content', value: debouncedContent } });
  }, [debouncedContent, moduleIndex, onModuleFieldChange]);

  useEffect(() => {
    onModuleFieldChange(moduleIndex, { target: { name: 'previewImage', value: debouncedPreviewImage } });
  }, [debouncedPreviewImage, moduleIndex, onModuleFieldChange]);

  useEffect(() => {
    onModuleFieldChange(moduleIndex, { target: { name: 'videoUrl', value: debouncedVideoUrl } });
  }, [debouncedVideoUrl, moduleIndex, onModuleFieldChange]);

  // Обработчик изменения локальных полей (без debouncing)
  const handleLocalFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setLocalFields(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Обработчик загрузки превью изображения
  const handlePreviewImageUpload = useCallback((e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error(t('courses:courseCreate.ui.imageTypeError'));
      e.target.value = '';
      return;
    }

    // Проверяем размер файла (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('courses:courseCreate.ui.imageSizeError'));
      e.target.value = '';
      return;
    }

    // Создаем превью для отображения
    const previewUrl = URL.createObjectURL(file);

    setLocalFields(prev => ({
      ...prev,
      previewImageFile: file,
      previewImagePreview: previewUrl,
      previewImage: previewUrl
    }));

    // Отправляем файл в родительский компонент
    onModuleFieldChange(moduleIndex, {
      target: {
        name: 'previewImageFile',
        value: file
      }
    });

    // Очищаем input после выбора файла
    e.target.value = '';
  }, [moduleIndex, onModuleFieldChange, t]);

  // Обработчик удаления превью изображения
  const handleRemovePreviewImage = useCallback(() => {
    // Очищаем URL превью если он был создан
    if (localFields.previewImagePreview) {
      URL.revokeObjectURL(localFields.previewImagePreview);
    }

    setLocalFields(prev => ({
      ...prev,
      previewImage: '',
      previewImageFile: null,
      previewImagePreview: null
    }));

    // Уведомляем родительский компонент
    onModuleFieldChange(moduleIndex, {
      target: {
        name: 'previewImage',
        value: ''
      }
    });
    onModuleFieldChange(moduleIndex, {
      target: {
        name: 'previewImageFile',
        value: null
      }
    });
  }, [localFields.previewImagePreview, moduleIndex, onModuleFieldChange]);

  // Обработчик для порядка модуля (без debouncing)
  const handleOrderChange = useCallback((e) => {
    onModuleOrderChange(moduleIndex, e.target.value);
  }, [moduleIndex, onModuleOrderChange]);

  // Открыть модальное окно подтверждения удаления урока
  const handleOpenDeleteLessonDialog = useCallback((lessonIndex, lesson) => {
    if (!lesson) {
      console.error('Урок не найден');
      return;
    }

    setDeleteLessonDialog({
      open: true,
      moduleId: module._id || null,
      lessonId: lesson._id || null,
      lessonIndex: lessonIndex,
      lessonTitle: lesson.title || t('courses:courseCreate.ui.lesson.unnamed')
    });
  }, [module._id, t]);

  // Закрыть модальное окно подтверждения удаления урока
  const handleCloseDeleteLessonDialog = useCallback(() => {
    setDeleteLessonDialog({
      open: false,
      moduleId: null,
      lessonId: null,
      lessonIndex: null,
      lessonTitle: ''
    });
  }, []);

  // Подтвердить удаление урока
  const handleConfirmDeleteLesson = useCallback(async () => {
    const { moduleId, lessonId, lessonIndex } = deleteLessonDialog;

    if (lessonIndex === null) {
      return;
    }

    // Если у урока есть ID и модуль сохранен, удаляем через API
    if (lessonId && moduleId) {
      try {
        await modulesAPI.removeLessonFromModule(moduleId, lessonId);
        toast.success(t('courses:courseCreate.ui.lesson.deleteSuccess'));
      } catch (error) {
        console.error('Ошибка при удалении урока из модуля:', error);
        toast.error(t('courses:courseCreate.ui.lesson.deleteError'));
        handleCloseDeleteLessonDialog();
        return; // Не удаляем из локального состояния, если не удалось удалить на сервере
      }
    }

    // Удаляем урок из локального состояния через callback
    // Передаем lessonId в параметре data для дополнительной информации
    onLessonChange(moduleIndex, 'remove', { lessonId }, lessonIndex);

    // Закрываем модальное окно
    handleCloseDeleteLessonDialog();
  }, [deleteLessonDialog, moduleIndex, onLessonChange, handleCloseDeleteLessonDialog, t]);

  // Обработчики для hover и клика на видео
  const handleMouseEnter = useCallback(() => {
    if (!showVideo && localFields.videoUrl) {
      setIsHovering(true);
      // Для обычных видео (не YouTube) запускаем через ref
      const video = videoRef.current;
      if (video && !isYouTubeUrl(localFields.videoUrl)) {
        playVideo(video);
      }
      // Для YouTube автопроигрывание работает через iframe параметр autoplay=1
    }
  }, [showVideo, localFields.videoUrl]);

  const handleMouseLeave = useCallback(() => {
    if (!showVideo) {
      setIsHovering(false);
      // Для обычных видео останавливаем воспроизведение
      const video = videoRef.current;
      if (video && !isYouTubeUrl(localFields.videoUrl)) {
        pauseAndResetVideo(video);
      }
      // Для YouTube iframe будет удален из DOM, что остановит воспроизведение
    }
  }, [showVideo, localFields.videoUrl]);

  const handlePreviewClick = useCallback(() => {
    if (localFields.videoUrl && !showVideo) {
      setShowVideo(true);
      setIsHovering(false);
    }
  }, [localFields.videoUrl, showVideo]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownOpen) {
        setStatusDropdownOpen(false);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  return (
    <>
    <Accordion
      elevation={0}
      key={moduleKey}
      sx={{ backgroundColor: 'background.paper' }}
      expanded={isExpanded}
      onChange={(event, expanded) => onToggleExpanded(moduleIndex, expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} component="div" sx={{ cursor: 'pointer' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6">
            {moduleIndex + 1}. {localFields.title || t('courses:courseCreate.ui.module_new')}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {module._id && (
              <span onClick={e => e.stopPropagation()}>
                <JsonPartExport data={module} title={module.title} type={t('courses:jsonExport.module')} />
              </span>
            )}
            <span
              onClick={(e) => {
                e.stopPropagation();
                onRemoveModule(moduleIndex);
              }}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#d32f2f' }}
            >
              <DeleteIcon fontSize="small" />
            </span>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* First row: Title, Order, Status */}
          <Grid item xs={12} md={5}>
            <TextField
              label={t('courses:courseCreate.ui.moduleTitle')}
              name="title"
              value={localFields.title}
              onChange={handleLocalFieldChange}
              fullWidth
              required
              key={`${moduleKey}-title`}
              placeholder="Installation and Setup"
            />
          </Grid>

          {/* Module Order - показывается только в контексте курса */}
          {showOrderField && (
            <Grid item xs={12} md={3}>
              <Select
                name="order"
                value={module.order || 0}
                onChange={handleOrderChange}
                label={t('courses:courseCreate.ui.module_order')}
                fullWidth
              >
                {Array.from({ length: totalModules }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          {/* Status */}
          <Grid item xs={12} md={showOrderField ? 4 : 7}>
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Status
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusDropdownOpen(!statusDropdownOpen);
                  }}
                  sx={({ palette }) => ({
                    p: '0 16px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '48px',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                    }
                  })}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      textTransform: 'capitalize'
                    }}
                  >
                    {isDraft ? t('common:status.draft') : t('common:status.published')}
                  </Typography>
                  <KeyboardArrowDownIcon sx={{
                    fontSize: 20,
                    color: 'text.secondary',
                    transition: 'transform 0.2s',
                    transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }} />
                </Box>
                {statusDropdownOpen && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 3,
                      zIndex: 3000,
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 1 }}>
                      <Box
                        onClick={() => {
                          onDraftToggle(moduleIndex, true);
                          setStatusDropdownOpen(false);
                        }}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Typography variant="body2">
                          {t('common:status.draft')}
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => {
                          onDraftToggle(moduleIndex, false);
                          setStatusDropdownOpen(false);
                        }}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Typography variant="body2">
                          {t('common:status.published')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Second row: Description & Content (left), Image & Video (right) */}
          <Grid item xs={12} lg={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label={t('courses:courseCreate.ui.moduleDescription')}
                name="description"
                value={localFields.description}
                onChange={handleLocalFieldChange}
                fullWidth
                multiline
                rows={4}
                key={`${moduleKey}-description`}
                placeholder="Describe the goals and content of this module..."
              />
              <TextField
                label={t('courses:courseCreate.ui.moduleContent')}
                name="content"
                value={localFields.content}
                onChange={handleLocalFieldChange}
                fullWidth
                multiline
                rows={4}
                required
                key={`${moduleKey}-content`}
                placeholder="Enter the main content for this module (optional)..."
              />
            </Box>
          </Grid>

          {/* Sidebar - 5 columns */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  {t('courses:courseCreate.ui.modulePreviewImage')}
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: '150px',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => document.getElementById(`module-${moduleIndex}-preview-image-upload`).click()}
                >
                  {(localFields.previewImagePreview || localFields.previewImage) ? (
                    <img
                      src={localFields.previewImagePreview || localFields.previewImage}
                      alt="Module Preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '150px',
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }}
                    />
                  ) : (
                    <>
                      <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('courses:courseCreate.ui.videoPreviewImage')}
                      </Typography>
                    </>
                  )}
                </Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id={`module-${moduleIndex}-preview-image-upload`}
                  type="file"
                  onChange={handlePreviewImageUpload}
                />
                {(localFields.previewImagePreview || localFields.previewImage) && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemovePreviewImage();
                    }}
                    sx={{ mt: 2 }}
                  >
                    {t('courses:courseCreate.ui.removeImage')}
                  </Button>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                    {t('courses:courseCreate.ui.or')}
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('courses:courseCreate.ui.pasteImageUrl')}
                  name="previewImage"
                  value={localFields.previewImage}
                  onChange={handleLocalFieldChange}
                  key={`${moduleKey}-preview-image`}
                />
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  {t('courses:courseCreate.ui.moduleVideoUrl')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="videoUrl"
                  value={localFields.videoUrl}
                  onChange={handleLocalFieldChange}
                  placeholder={t('courses:courseCreate.ui.pastePromoVideoUrl')}
                  key={`${moduleKey}-videoUrl`}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => setModulePreviewOpen(!modulePreviewOpen)}
                    startIcon={modulePreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      py: 1.5,
                      borderWidth: 2,
                      fontWeight: 600,
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    {modulePreviewOpen ? t('courses:courseCreate.ui.hideModulePreview') : t('courses:courseCreate.ui.showModulePreview')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

        </Grid>

        {/* Module Preview Section */}
        {modulePreviewOpen && (
          <Box sx={{ my: 4 }}>
            <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
              {/* Side Module Card 1 */}
              <Grid item xs="auto">
                <Box
                  sx={{
                    position: 'relative',
                    width: '260px',
                    height: '470px',
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  {/* Background Image */}
                  {(localFields.previewImagePreview || localFields.previewImage) ? (
                    <Box
                      component="img"
                      src={localFields.previewImagePreview || localFields.previewImage}
                      alt="Module"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    />
                  )}
                  {/* Hover overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      opacity: 1,
                      transition: 'opacity 0.3s ease',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                  {/* Module Info */}
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, color: 'white', zIndex: 2 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                      Module {moduleIndex}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                      {localFields.title || t('courses:courseCreate.ui.moduleTitle')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Active Module Card (Center) */}
              <Grid item xs="auto">
                <Box
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={handlePreviewClick}
                  sx={{
                    position: 'relative',
                    width: '260px',
                    height: '470px',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: (theme) => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    transform: { md: 'scale(1.05)' },
                    zIndex: 10,
                    transition: 'all 0.3s ease',
                    cursor: localFields.videoUrl && !showVideo ? 'pointer' : 'default'
                  }}
                >
                  {/* Preview Image - background layer */}
                  {!showVideo && (localFields.previewImagePreview || localFields.previewImage) && (
                    <Box
                      component="img"
                      src={localFields.previewImagePreview || localFields.previewImage}
                      alt="Module Preview"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 1
                      }}
                    />
                  )}

                  {/* Hover Video - shows on hover */}
                  {isHovering && !showVideo && localFields.videoUrl && (() => {
                    const isYouTube = isYouTubeUrl(localFields.videoUrl);

                    if (isYouTube) {
                      // YouTube hover preview
                      const youtubeId = getYouTubeId(localFields.videoUrl);

                      return (
                        <Box
                          component="iframe"
                          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&modestbranding=1&rel=0`}
                          title="YouTube video preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                            border: 'none',
                            pointerEvents: 'none'
                          }}
                        />
                      );
                    } else {
                      // Regular video hover
                      return (
                        <video
                          ref={videoRef}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 2
                          }}
                          loop
                          muted
                          playsInline
                          src={localFields.videoUrl}
                        />
                      );
                    }
                  })()}

                  {/* Video - shows on click */}
                  {showVideo && localFields.videoUrl && (() => {
                    const isYouTube = isYouTubeUrl(localFields.videoUrl);

                    if (isYouTube) {
                      // Extract YouTube ID
                      const youtubeId = getYouTubeId(localFields.videoUrl);

                      return (
                        <Box
                          component="iframe"
                          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&controls=1`}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                            border: 'none'
                          }}
                        />
                      );
                    } else {
                      // Regular video file
                      return (
                        <video
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 2
                          }}
                          controls
                          autoPlay
                          playsInline
                          src={localFields.videoUrl}
                        />
                      );
                    }
                  })()}

                  {/* Fallback gradient (only if no preview image) */}
                  {!(localFields.previewImagePreview || localFields.previewImage) && !localFields.videoUrl && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: (theme) => `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.8
                        )} 0%, ${alpha(
                          theme.palette.secondary.main,
                          0.8
                        )} 100%)`
                      }}
                    />
                  )}

                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                      pointerEvents: 'none',
                      zIndex: 3
                    }}
                  />

                  {/* Module Info */}
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2.5, color: 'white', zIndex: 4 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9, display: 'block', mb: 0.5 }}>
                      Module {moduleIndex + 1}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                      {localFields.title || t('courses:courseCreate.ui.moduleTitle')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Side Module Card 2 */}
              <Grid item xs="auto">
                <Box
                  sx={{
                    position: 'relative',
                    width: '260px',
                    height: '470px',
                    borderRadius: 3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  {/* Background Image */}
                  {(localFields.previewImagePreview || localFields.previewImage) ? (
                    <Box
                      component="img"
                      src={localFields.previewImagePreview || localFields.previewImage}
                      alt="Module"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    />
                  )}
                  {/* Hover overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      opacity: 1,
                      transition: 'opacity 0.3s ease',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                  {/* Module Info */}
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2, color: 'white', zIndex: 2 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                      Module {moduleIndex + 2}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                      {localFields.title || t('courses:courseCreate.ui.moduleTitle')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Divider */}
        <Box sx={{ my: 4, borderBottom: '1px solid', borderColor: 'divider' }} />

        {(
          <Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 2 }}>
              <Typography variant="h6" component="h4">
                {t('courses:courseCreate.ui.moduleLesson.title')} {moduleIndex + 1}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <LessonJsonImport
                  moduleTitle={module.title}
                  onSuccess={(lessonData) => {
                    onLessonChange(moduleIndex, 'add', lessonData);
                    toast.success(
                      t('courses:courseCreate.ui.lesson.importSuccess', { moduleTitle: module.title })
                    );
                  }}
                />
                <LessonsArrayJsonImport
                  moduleIndex={moduleIndex}
                  onSuccess={(moduleIdx, lessonsArray) => {
                    onLessonChange(moduleIndex, 'addArray', lessonsArray);
                    toast.success(
                      t('courses:courseCreate.ui.lesson.importArraySuccess', { moduleTitle: module.title })
                    );
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => onAddLesson(moduleIndex)}
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: '122px',
                    minHeight: '35px',
                    lineHeight: 1,
                    py: 0.5,
                    px: 1.5,
                  }}
                >
                  {t('courses:courseCreate.ui.moduleLesson.add')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LibraryAddIcon />}
                  onClick={() => onOpenLessonSelector(moduleIndex)}
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: '122px',
                    minHeight: '35px',
                    lineHeight: 1,
                    py: 0.5,
                    px: 1.5,
                    borderColor: 'button.secondaryBorder',
                    color: 'button.secondaryText',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      borderColor: 'button.secondaryBorder',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                >
                  {t('courses:courseCreate.ui.moduleLesson.selectFromLibrary')}
                </Button>
              </Box>
            </Box>
            {module.lessons &&
              module.lessons.map((lesson, lessonIndex) => (
                <Box key={lesson._id || `lesson-${moduleIndex}-${lessonIndex}`} sx={{ mb: 3 }}>
                  <LessonEditor
                    lesson={lesson}
                    onChange={(updatedLesson) => onLessonChange(moduleIndex, 'update', updatedLesson, lessonIndex)}
                    onProfessionsSync={onProfessionsSync}
                    uniqueId={`${moduleIndex}-${lessonIndex}`}
                    header={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" component="h5">
                          {t('courses:courseCreate.ui.moduleLesson.lessonNumber', { number: lessonIndex + 1 })}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {lesson._id && (
                            <JsonPartExport data={lesson} title={lesson.title} type={t('courses:jsonExport.lesson')} />
                          )}
                          <span
                            onClick={() => handleOpenDeleteLessonDialog(lessonIndex, lesson)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </span>
                        </Box>
                      </Box>
                    }
                    isAdminOrTeacher={(user.userMeta?.roles || user.roles || []).includes('admin') || (user.userMeta?.roles || user.roles || []).includes('teacher')}
                  />
                </Box>
              ))}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => onAddLesson(moduleIndex)} sx={{ mt: 1, mb: 3 }} fullWidth>
              {t('courses:courseCreate.ui.moduleLesson.add')}
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>

    {/* Модальное окно подтверждения удаления урока */}
    {deleteLessonDialog.open && (
      <Dialog
        open={deleteLessonDialog.open}
        onClose={handleCloseDeleteLessonDialog}
        aria-labelledby="delete-lesson-dialog-title"
        aria-describedby="delete-lesson-dialog-description"
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '16px',
            p: 1,
          },
        }}
      >
        <DialogTitle id="delete-lesson-dialog-title">
          {t('courses:courseCreate.ui.lesson.deleteLessonDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-lesson-dialog-description">
            {t('courses:courseCreate.ui.lesson.deleteLessonDialogMessage', { title: deleteLessonDialog.lessonTitle })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteLessonDialog} color="primary">
            {t('courses:courseCreate.ui.lesson.deleteLessonDialogCancel')}
          </Button>
          <Button onClick={handleConfirmDeleteLesson} color="error" variant="contained">
            {t('courses:courseCreate.ui.lesson.deleteLessonDialogConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    )}
  </>
  );
});

ModuleCard.displayName = 'ModuleCard';

export default ModuleCard;

